import React, { useState, useEffect, useCallback } from 'react';
import { getApiUrl } from '@/config/api';
import { authenticatedFetchWithRetry } from '@/utils/apiUtils';
import { Plus, Check, Trash2, Edit2, X } from 'lucide-react';
import type { InjuryRecordEntry } from '@/types';
import { painColor } from '@/components/shared/BodyRegionMap';
import { BODY_REGIONS, REGION_FULL } from '@/components/team-health/bodyRegions';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SEVERITY_CONFIG: Record<string, { label: string; color: string; bg: string; bgDark: string }> = {
  minor: { label: 'Minor', color: 'text-blue-500', bg: 'bg-blue-500/10', bgDark: 'bg-blue-500/20' },
  moderate: { label: 'Moderate', color: 'text-amber-500', bg: 'bg-amber-500/10', bgDark: 'bg-amber-500/20' },
  severe: { label: 'Severe', color: 'text-red-500', bg: 'bg-red-500/10', bgDark: 'bg-red-500/20' },
};

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; bgDark: string }> = {
  active: { label: 'Active', color: 'text-red-500', bg: 'bg-red-500/10', bgDark: 'bg-red-500/20' },
  improving: { label: 'Improving', color: 'text-amber-500', bg: 'bg-amber-500/10', bgDark: 'bg-amber-500/20' },
  resolved: { label: 'Resolved', color: 'text-green-500', bg: 'bg-green-500/10', bgDark: 'bg-green-500/20' },
};

// ---------------------------------------------------------------------------
// Form state
// ---------------------------------------------------------------------------

interface InjuryFormData {
  body_region: string;
  injury_name: string;
  severity: string;
  status: string;
  injury_date: string;
  resolved_date: string;
  notes: string;
}

const EMPTY_FORM: InjuryFormData = {
  body_region: 'lower_back',
  injury_name: '',
  severity: 'minor',
  status: 'active',
  injury_date: new Date().toISOString().split('T')[0],
  resolved_date: '',
  notes: '',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface InjuryHistoryPanelProps {
  athleteId: number;
  isDark: boolean;
  currentPainByRegion?: Record<string, number>;
}

const InjuryHistoryPanel: React.FC<InjuryHistoryPanelProps> = ({ athleteId, isDark, currentPainByRegion = {} }) => {
  const [injuries, setInjuries] = useState<InjuryRecordEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<InjuryFormData>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const fetchInjuries = useCallback(async () => {
    const result = await authenticatedFetchWithRetry<{ injuries: InjuryRecordEntry[] }>(
      getApiUrl(`/api/injury-history/${athleteId}/`),
      { method: 'GET' },
      'fetching injury history',
    );
    if (result.success && result.data) {
      // Backend error wrapped in 200 (CloudFront masking workaround)
      if ((result.data as any).status === 'error') {
        setError((result.data as any).error || 'Failed to load injury history');
        setLoading(false);
        return;
      }
      // Sort: active first, then by injury_date descending
      const sorted = [...result.data.injuries].sort((a, b) => {
        if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
        return new Date(b.injury_date).getTime() - new Date(a.injury_date).getTime();
      });
      setInjuries(sorted);
    } else {
      setError(result.error || 'Failed to load injury history');
    }
    setLoading(false);
  }, [athleteId]);

  useEffect(() => {
    fetchInjuries();
  }, [fetchInjuries]);

  const handleSave = async () => {
    setSaving(true);
    setError('');

    const payload: Record<string, string | null> = {
      body_region: formData.body_region,
      injury_name: formData.injury_name || '',
      severity: formData.severity,
      status: formData.status,
      injury_date: formData.injury_date,
      notes: formData.notes,
    };
    if (formData.resolved_date) {
      payload.resolved_date = formData.resolved_date;
    } else {
      payload.resolved_date = null;
    }

    const isEdit = editingId !== null;
    const url = isEdit
      ? getApiUrl(`/api/injury-history/${athleteId}/${editingId}/`)
      : getApiUrl(`/api/injury-history/${athleteId}/`);

    const result = await authenticatedFetchWithRetry(
      url,
      {
        method: isEdit ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
      isEdit ? 'updating injury record' : 'creating injury record',
    );

    if (result.success) {
      // Backend error wrapped in 200 (CloudFront masking workaround)
      if ((result.data as any)?.status === 'error') {
        setError((result.data as any).error || 'Failed to save');
        setSaving(false);
        return;
      }
      setShowForm(false);
      setEditingId(null);
      setFormData(EMPTY_FORM);
      await fetchInjuries();
    } else {
      setError(result.error || 'Failed to save');
    }
    setSaving(false);
  };

  const handleStatusChange = async (id: number, newStatus: string) => {
    const payload: Record<string, string | null> = { status: newStatus };
    if (newStatus === 'resolved') {
      payload.resolved_date = new Date().toISOString().split('T')[0];
    }
    const result = await authenticatedFetchWithRetry(
      getApiUrl(`/api/injury-history/${athleteId}/${id}/`),
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
      'updating injury status',
    );
    if (result.success && (result.data as any)?.status !== 'error') {
      await fetchInjuries();
    }
  };

  const handleDelete = async (id: number) => {
    // Direct fetch — DELETE returns 204 No Content (empty body),
    // which authenticatedFetchWithRetry can't parse as JSON.
    const token = localStorage.getItem('access');
    const res = await fetch(getApiUrl(`/api/injury-history/${athleteId}/${id}/`), {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (res.ok) {
      setConfirmDeleteId(null);
      await fetchInjuries();
    }
  };

  const startEdit = (injury: InjuryRecordEntry) => {
    setEditingId(injury.id);
    setFormData({
      body_region: injury.body_region,
      injury_name: (injury as any).injury_name || '',
      severity: injury.severity,
      status: (injury as any).status || (injury.is_active ? 'active' : 'resolved'),
      injury_date: injury.injury_date,
      resolved_date: injury.resolved_date || '',
      notes: injury.notes,
    });
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData(EMPTY_FORM);
  };

  const inputClass = `w-full px-2 py-1.5 text-xs rounded-none border ${
    isDark
      ? 'bg-neutral-800 border-neutral-600 text-white'
      : 'bg-white border-gray-300 text-gray-900'
  } focus:outline-none focus:ring-1 focus:ring-blue-500`;

  if (loading) {
    return (
      <div className="mb-5">
        <h4 className="text-sm font-semibold cgm-text-primary mb-2">Injury History</h4>
        <div className="text-xs cgm-text-secondary">Loading...</div>
      </div>
    );
  }

  return (
    <div className="mb-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold cgm-text-primary">Injury History</h4>
        {!showForm && (
          <button
            onClick={() => { setFormData(EMPTY_FORM); setEditingId(null); setShowForm(true); }}
            className={`flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-none border transition-colors ${
              isDark
                ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-700'
                : 'border-gray-300 text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Plus className="w-3 h-3" />
            Add Injury
          </button>
        )}
      </div>

      {error && (
        <div className="text-xs text-red-500 mb-2">{error}</div>
      )}

      {/* Inline Form */}
      {showForm && (
        <div className={`p-3 mb-3 border ${isDark ? 'bg-neutral-800/50 border-neutral-700' : 'bg-gray-50 border-gray-200'}`}>
          <div className="text-xs font-semibold cgm-text-primary mb-2">
            {editingId ? 'Edit Injury' : 'New Injury Record'}
          </div>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div>
              <label className="text-[10px] cgm-text-secondary block mb-0.5">Body Region</label>
              <select
                value={formData.body_region}
                onChange={(e) => setFormData({ ...formData, body_region: e.target.value })}
                className={inputClass}
              >
                {BODY_REGIONS.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] cgm-text-secondary block mb-0.5">Injury Name (optional)</label>
              <input
                type="text"
                value={formData.injury_name}
                onChange={(e) => setFormData({ ...formData, injury_name: e.target.value })}
                placeholder="e.g. IT band syndrome"
                className={inputClass}
              />
            </div>
            <div>
              <label className="text-[10px] cgm-text-secondary block mb-0.5">Severity</label>
              <select
                value={formData.severity}
                onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                className={inputClass}
              >
                <option value="minor">Minor</option>
                <option value="moderate">Moderate</option>
                <option value="severe">Severe</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] cgm-text-secondary block mb-0.5">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className={inputClass}
              >
                <option value="active">Active</option>
                <option value="improving">Improving</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] cgm-text-secondary block mb-0.5">Injury Date</label>
              <input
                type="date"
                value={formData.injury_date}
                onChange={(e) => setFormData({ ...formData, injury_date: e.target.value })}
                className={inputClass}
              />
            </div>
            <div>
              <label className="text-[10px] cgm-text-secondary block mb-0.5">Resolved Date (optional)</label>
              <input
                type="date"
                value={formData.resolved_date}
                onChange={(e) => setFormData({ ...formData, resolved_date: e.target.value })}
                className={inputClass}
              />
            </div>
          </div>
          <div className="mb-2">
            <label className="text-[10px] cgm-text-secondary block mb-0.5">Notes</label>
            <input
              type="text"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Brief description of the injury"
              className={inputClass}
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSave}
              disabled={saving || !formData.injury_date || !formData.body_region}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-none bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Saving...' : editingId ? 'Update' : 'Save'}
            </button>
            <button
              onClick={cancelForm}
              className={`px-3 py-1.5 text-xs font-medium rounded-none border transition-colors ${
                isDark ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-700' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
              }`}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Injury List */}
      {injuries.length === 0 ? (
        <div className="text-xs cgm-text-secondary">No injury history recorded</div>
      ) : (
        <div className="space-y-1.5">
          {injuries.map((injury) => {
            const sev = SEVERITY_CONFIG[injury.severity] || SEVERITY_CONFIG.minor;
            const injuryStatus = (injury as any).status || (injury.is_active ? 'active' : 'resolved');
            const statusCfg = STATUS_CONFIG[injuryStatus] || STATUS_CONFIG.active;
            const regionLabel = REGION_FULL[injury.body_region] || injury.body_region;
            const injuryName = (injury as any).injury_name;

            return (
              <div
                key={injury.id}
                className={`flex items-center justify-between p-2 border ${
                  isDark ? 'border-neutral-700/50 bg-neutral-800/30' : 'border-gray-200 bg-gray-50/50'
                } ${injuryStatus === 'resolved' ? 'opacity-60' : ''}`}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-medium cgm-text-primary truncate">
                        {injuryName || regionLabel}
                      </span>
                      {injuryName && (
                        <span className="text-[10px] cgm-text-secondary">{regionLabel}</span>
                      )}
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 ${sev.color} ${isDark ? sev.bgDark : sev.bg}`}>
                        {sev.label}
                      </span>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 ${statusCfg.color} ${isDark ? statusCfg.bgDark : statusCfg.bg}`}>
                        {statusCfg.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] cgm-text-secondary">
                        {new Date(injury.injury_date).toLocaleDateString()}
                      </span>
                      <span className="text-[10px] cgm-text-secondary">
                        {injury.days_since}d ago
                      </span>
                      {injury.notes && (
                        <span className="text-[10px] cgm-text-secondary truncate max-w-[150px]">{injury.notes}</span>
                      )}
                      {currentPainByRegion[injury.body_region] !== undefined && (
                        <span
                          className="text-[10px] font-semibold"
                          style={{ color: painColor(currentPainByRegion[injury.body_region]) }}
                        >
                          Currently {currentPainByRegion[injury.body_region]}/10
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                  {injuryStatus === 'active' && (
                    <button
                      onClick={() => handleStatusChange(injury.id, 'improving')}
                      title="Mark as improving"
                      className={`p-1 transition-colors ${isDark ? 'text-amber-400 hover:bg-amber-500/20' : 'text-amber-600 hover:bg-amber-500/10'}`}
                    >
                      <Check className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {(injuryStatus === 'active' || injuryStatus === 'improving') && (
                    <button
                      onClick={() => handleStatusChange(injury.id, 'resolved')}
                      title="Mark as resolved"
                      className={`p-1 transition-colors ${isDark ? 'text-green-400 hover:bg-green-500/20' : 'text-green-600 hover:bg-green-500/10'}`}
                    >
                      <Check className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button
                    onClick={() => startEdit(injury)}
                    title="Edit"
                    className={`p-1 transition-colors ${isDark ? 'text-neutral-400 hover:bg-neutral-700' : 'text-gray-400 hover:bg-gray-200'}`}
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  {confirmDeleteId === injury.id ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleDelete(injury.id)}
                        className="p-1 text-red-500 hover:bg-red-500/20 transition-colors"
                        title="Confirm delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => setConfirmDeleteId(null)}
                        className={`p-1 transition-colors ${isDark ? 'text-neutral-400 hover:bg-neutral-700' : 'text-gray-400 hover:bg-gray-200'}`}
                        title="Cancel"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmDeleteId(injury.id)}
                      title="Delete"
                      className={`p-1 transition-colors ${isDark ? 'text-neutral-400 hover:bg-neutral-700' : 'text-gray-400 hover:bg-gray-200'}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default InjuryHistoryPanel;
